import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Mail, CheckCircle, AlertCircle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface EmailVerificationGuardProps {
  children: React.ReactNode;
  userEmail?: string;
}

export const EmailVerificationGuard = ({ children, userEmail }: EmailVerificationGuardProps) => {
  const [isResending, setIsResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const { toast } = useToast();

  const handleResendVerification = async () => {
    if (!userEmail) return;

    setIsResending(true);
    try {
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email: userEmail,
        options: {
          emailRedirectTo: `${window.location.origin}/auth/confirm`,
        }
      });

      if (error) throw error;

      setResendSuccess(true);
      toast({
        title: "Verification email sent",
        description: "Please check your inbox for a new verification email.",
      });
    } catch (error: any) {
      console.error('Error resending verification:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to resend verification email.",
        variant: "destructive",
      });
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background to-secondary/20 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mb-4">
            <Mail className="w-6 h-6 text-primary" />
          </div>
          <CardTitle className="text-2xl">Verify Your Email</CardTitle>
          <CardDescription>
            Check your inbox to verify your email address before continuing
          </CardDescription>
        </CardHeader>
        
        <CardContent className="space-y-4">
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              You need to verify your email address before you can access your account.
              After verification, your account will be reviewed by an administrator.
              Check your inbox and click the verification link.
            </AlertDescription>
          </Alert>

          {userEmail && (
            <div className="text-center text-sm text-muted-foreground">
              Verification email sent to: <strong>{userEmail}</strong>
            </div>
          )}

          {resendSuccess && (
            <Alert>
              <CheckCircle className="h-4 w-4" />
              <AlertDescription>
                A new verification email has been sent to your inbox.
              </AlertDescription>
            </Alert>
          )}

          <div className="space-y-2">
            {userEmail && (
              <Button 
                onClick={handleResendVerification}
                disabled={isResending}
                variant="outline" 
                className="w-full"
              >
                {isResending ? "Sending..." : "Resend verification email"}
              </Button>
            )}
            
            <Button 
              onClick={() => window.location.reload()} 
              className="w-full"
              variant="default"
            >
              I've verified my email
            </Button>
          </div>

          <div className="text-center text-sm text-muted-foreground">
            <p>
              Didn't receive the email? Check your spam folder or{' '}
              <button 
                onClick={handleResendVerification}
                className="text-primary hover:underline"
                disabled={isResending}
              >
                resend verification email
              </button>
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};